import axios from 'axios';
import { config } from '../config/environment';
import { 
  generateRobustDeviceHash, 
  generateRobustDeviceName, 
  getRobustDevicePlatform 
} from '../utils/robustDeviceFingerprint';

// Create axios instance
const apiClient = axios.create({
  baseURL: config.apiUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use((axiosConfig) => {
  const token = localStorage.getItem('quiz_token');
  if (token) {
    axiosConfig.headers.Authorization = `Bearer ${token}`;
  }
  
  // Debug logging
  if (config.debug) {
    console.log(`[API] ${axiosConfig.method?.toUpperCase()} ${axiosConfig.url}`, {
      data: axiosConfig.data,
      params: axiosConfig.params,
      headers: axiosConfig.headers,
    });
  }
  
  return axiosConfig;
});

// Response interceptor to handle errors
apiClient.interceptors.response.use(
  (response) => {
    // Debug logging
    if (config.debug) {
      console.log(`[API] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`, {
        data: response.data,
      });
    }
    return response;
  },
  (error) => {
    // Debug logging
    if (config.debug) {
      console.error(`[API] ${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.response?.status}`, {
        error: error.response?.data,
        message: error.message,
      });
    }
    
    if (error.response?.status === 401) {
      // Token expired or invalid - only redirect if not already on login page
      const currentPath = window.location.pathname;
      if (currentPath !== '/login' && currentPath !== '/register') {
        localStorage.removeItem('quiz_token');
        localStorage.removeItem('quiz_refresh_token');
        window.location.href = '/login';
      }
    }
    
    // For 403 errors, we want to show the specific error message
    // Don't redirect, let the component handle it
    return Promise.reject(error);
  }
);

// Types
export interface User {
  id: string;
  email: string;
  username: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  isActive: boolean;
  isBlocked: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Subject {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  questionCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface Question {
  id: string;
  subjectId: string;
  text: string;
  difficulty: string;
  explanation?: string;
  isActive: boolean;
  answerOptions: AnswerOption[];
  createdAt: string;
  updatedAt: string;
}

export interface AnswerOption {
  id: string;
  text: string;
  isCorrect: boolean;
  order: number;
}

export interface QuizSession {
  id: string;
  userId: string;
  subjectId: string;
  status: 'active' | 'completed' | 'paused';
  totalQuestions: number;
  correctAnswers: number;
  score: number;
  startedAt: string;
  completedAt?: string;
  difficulty: number;
  timeSpent: number;
}

export interface QuizAnswer {
  id: string;
  questionId: string;
  answerId: string;
  isCorrect: boolean;
  timeSpent: number;
  answeredAt: string;
}

export interface QuizStats {
  totalQuestions: number;
  correctAnswers: number;
  incorrectAnswers: number;
  score: number;
  accuracy: number;
  timeSpent: number;
  averageTimePerQuestion: number;
}

export interface UserPersonalStats {
  completedSessions: number;
  totalTimeSpent: number;
  averageAccuracy: number;
  totalQuestionsAnswered: number;
  totalCorrectAnswers: number;
  totalWrongAnswers: number;
  averageTimePerQuestion: number;
}

// API functions
export const api = {
  // Auth
  async login(email: string, activationCode: string) {
    try {
      console.log('🔐 [LOGIN] Iniziando login con:', { email, activationCode });
      
      const deviceHash = await generateRobustDeviceHash();
      const deviceName = await generateRobustDeviceName();
      const platform = getRobustDevicePlatform();
      
      console.log('📱 [LOGIN] Device info:', { deviceHash, deviceName, platform });
      
      const response = await apiClient.post('/auth/login', { 
        email, 
        activationCode,
        deviceHash,
        deviceName,
        platform
      });
      
      console.log('✅ [LOGIN] Login successful:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [LOGIN] Login failed:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        config: error.config
      });
      throw error;
    }
  },

  async register(email: string, firstName: string, lastName: string, phoneNumber: string, activationCode: string) {
    const deviceHash = await generateRobustDeviceHash();
    const deviceName = await generateRobustDeviceName();
    const platform = getRobustDevicePlatform();
    
    const response = await apiClient.post('/auth/register', { 
      email, 
      firstName,
      lastName,
      phoneNumber,
      activationCode,
      deviceHash,
      deviceName,
      platform
    });
    return response.data;
  },

  async logout() {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  async getProfile(token?: string) {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await apiClient.get('/auth/profile', { headers });
    return response.data;
  },

  async getPersonalStats(): Promise<UserPersonalStats> {
    console.log('🔍🔍🔍 [API] Chiamando getPersonalStats...');
    const token = localStorage.getItem('quiz_token');
    console.log('🔑🔑🔑 [API] Token utilizzato per getPersonalStats:', token ? `${token.substring(0, 20)}...` : 'Nessun token');
    const response = await apiClient.get('/users/personal-stats');
    console.log('📊📊📊 [API] Risposta getPersonalStats:', response.data);
    return response.data;
  },

  // Subjects
  async getSubjects(): Promise<Subject[]> {
    const response = await apiClient.get('/subjects/active');
    return response.data;
  },

  async getSubject(id: string): Promise<Subject> {
    const response = await apiClient.get(`/subjects/${id}`);
    return response.data;
  },

  // Questions
  async getRandomQuestions(subjectId: string, count: number = 10): Promise<Question[]> {
    const response = await apiClient.get(`/questions/random/${subjectId}?count=${count}`);
    return response.data;
  },

  async preloadQuestions(subjectId: string, count: number = 50): Promise<Question[]> {
    const response = await apiClient.post('/quiz-sessions/cache/preload', {
      subjectId,
      count,
    });
    return response.data;
  },

  async getCacheStatus(subjectId: string) {
    const response = await apiClient.get(`/quiz-sessions/cache/status/${subjectId}`);
    return response.data;
  },

  // Quiz Sessions
  async createQuizSession(subjectId: string, difficulty: number = 1): Promise<QuizSession> {
    const response = await apiClient.post('/quiz-sessions', {
      subjectId,
      difficulty,
    });
    return response.data;
  },

  async getQuizSession(sessionId: string): Promise<QuizSession> {
    const response = await apiClient.get(`/quiz-sessions/${sessionId}`);
    return response.data;
  },

  async submitQuizAnswer(sessionId: string, questionId: string, answerId: string, timeSpent: number): Promise<QuizAnswer> {
    const response = await apiClient.post(`/quiz-sessions/${sessionId}/answers`, {
      questionId,
      answerId,
      timeSpent,
    });
    return response.data;
  },

  async submitQuizAnswers(sessionId: string, answers: Array<{
    questionId: string;
    answerId: string;
    timeSpent: number;
  }>): Promise<QuizAnswer[]> {
    const response = await apiClient.post(`/quiz-sessions/${sessionId}/submit`, {
      answers,
    });
    return response.data;
  },

  async getQuizStats(sessionId: string): Promise<QuizStats> {
    const response = await apiClient.get(`/quiz-sessions/${sessionId}/stats`);
    return response.data;
  },

  async getWrongAnswers(sessionId: string): Promise<Array<{
    question: Question;
    userAnswer: AnswerOption;
    correctAnswer: AnswerOption;
    timeSpent: number;
  }>> {
    const response = await apiClient.get(`/quiz-sessions/${sessionId}/wrong-answers`);
    return response.data;
  },

  // Cache management
  async clearCache(): Promise<void> {
    await apiClient.post('/quiz-sessions/cache/clear');
  },

  // Health check
  async getHealth() {
    const response = await apiClient.get('/health');
    return response.data;
  },
};
